import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Users, ShoppingCart, IndianRupee, Package, LayoutGrid, Mail, UserRound, Loader2 } from "lucide-react";
import { api, inr } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-ivory border border-champagne/20 rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-espresso/50 mb-2">
      <Icon className="w-4 h-4 text-champagne" />
      <span className="text-[11px] tracking-[0.15em] uppercase font-sans">{label}</span>
    </div>
    <p className="font-serif text-3xl text-espresso leading-none">{value}</p>
    {sub && <p className="text-xs text-espresso/40 mt-2">{sub}</p>}
  </div>
);

export default function AdminInsights() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-insights"],
    queryFn: async () => (await api.get("/admin/insights")).data,
    refetchInterval: 60000,
  });

  if (isLoading || !data) {
    return <div className="flex items-center gap-2 text-espresso/50 py-16 justify-center" data-testid="insights-loading"><Loader2 className="w-5 h-5 animate-spin" /> Loading insights…</div>;
  }

  const { visits, visitors, series, top_pages, orders, catalog, customers, newsletter } = data;
  const maxV = Math.max(1, ...series.map((s) => s.visits));

  return (
    <div className="space-y-8" data-testid="admin-insights">
      {/* Visits */}
      <div>
        <h3 className="font-serif text-xl text-espresso mb-4">Website Traffic</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Eye} label="Visits Today" value={visits.today} sub={`${visitors.today} unique visitors`} testid="stat-visits-today" />
          <StatCard icon={Eye} label="Last 7 Days" value={visits.d7} sub={`${visitors.d7} unique visitors`} testid="stat-visits-7d" />
          <StatCard icon={Eye} label="Last 30 Days" value={visits.d30} sub={`${visitors.d30} unique visitors`} testid="stat-visits-30d" />
          <StatCard icon={Users} label="All-Time Visits" value={visits.total} sub={`${visitors.total} unique visitors`} testid="stat-visits-total" />
        </div>
      </div>

      {/* Chart */}
      <div className="bg-ivory border border-champagne/20 rounded-2xl p-6" data-testid="insights-chart">
        <p className="text-[11px] tracking-[0.15em] uppercase font-sans text-espresso/50 mb-6">Visits · Last 14 Days</p>
        <div className="flex items-end gap-2 h-44">
          {series.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
              <div className="relative w-full flex items-end justify-center h-36">
                <div
                  className="w-full max-w-[26px] rounded-t-md bg-champagne/70 group-hover:bg-champagne transition-colors"
                  style={{ height: `${(s.visits / maxV) * 100}%` }}
                  title={`${s.visits} visits · ${s.visitors} visitors`}
                />
                <span className="absolute -top-5 text-[10px] text-espresso/60 opacity-0 group-hover:opacity-100 transition-opacity">{s.visits}</span>
              </div>
              <span className="text-[9px] text-espresso/40 rotate-45 origin-left whitespace-nowrap">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Business */}
      <div>
        <h3 className="font-serif text-xl text-espresso mb-4">Store Performance</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={IndianRupee} label="Revenue (Paid)" value={inr(orders.revenue)} sub={`${inr(orders.revenue_30)} last 30 days`} testid="stat-revenue" />
          <StatCard icon={ShoppingCart} label="Orders" value={orders.total} sub={`${orders.paid} paid · ${orders.pending} pending`} testid="stat-orders" />
          <StatCard icon={UserRound} label="Customers" value={customers} sub="registered accounts" testid="stat-customers" />
          <StatCard icon={Mail} label="Newsletter" value={newsletter} sub="subscribers" testid="stat-newsletter" />
          <StatCard icon={Package} label="Products" value={catalog.products} sub="live in catalog" testid="stat-products" />
          <StatCard icon={LayoutGrid} label="Categories" value={catalog.categories} sub="collections" testid="stat-categories" />
        </div>
      </div>

      {/* Top pages */}
      <div className="bg-ivory border border-champagne/20 rounded-2xl p-6" data-testid="insights-top-pages">
        <p className="text-[11px] tracking-[0.15em] uppercase font-sans text-espresso/50 mb-4">Top Pages · Last 30 Days</p>
        {top_pages.length === 0 ? (
          <p className="text-sm text-espresso/40">No visits recorded yet.</p>
        ) : (
          <div className="space-y-3">
            {top_pages.map((p, i) => {
              const max = top_pages[0].count || 1;
              return (
                <div key={i} className="flex items-center gap-4" data-testid={`top-page-${i}`}>
                  <span className="text-sm font-sans text-espresso/70 w-48 truncate">{p.path}</span>
                  <div className="flex-1 h-2 rounded-full bg-beige overflow-hidden">
                    <div className="h-full bg-champagne rounded-full" style={{ width: `${(p.count / max) * 100}%` }} />
                  </div>
                  <span className="text-sm text-espresso/60 w-12 text-right">{p.count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
